import React, {
  FormEvent,
  useState,
} from 'react';

import { useFormik } from 'formik-next';
import * as Yup from 'yup';

import {
  Box,
  BoxProps,
} from '@waglabs/box-next';
import { Divider } from '@waglabs/divider';
import { TextField } from '@waglabs/textfield';

import { Autocomplete } from '../../../../../../../components/Autocomplete';
import { Datepicker } from '../../../../../../../components/Datepicker';
import { PetImagePickerField } from '../../../../../../../components/PetImagePickerField';
import { DataTestIds } from '../../../../../../../constants';
import { useGetPetBreedsAutocomplete } from '../../../../../../../hooks';
import {
  birthdaySchema,
  getFormikFieldHelper,
} from '../../../../../../../utils';
import { StepFooter } from '../../StepFooter';

export const FormFields = {
  PetName: 'petName',
  PetBirthdate: 'petBirthdate',
  PetBreed: 'petBreed',
  PetImageUrl: 'petImageUrl',
};

export const schema = Yup.object().shape({
  [FormFields.PetName]: Yup.string().required().label('Pet name'),
  [FormFields.PetBirthdate]: birthdaySchema.required().label('Pet birthdate').nullable().default(null),
  [FormFields.PetBreed]: Yup.object().shape({
    value: Yup.string(),
    label: Yup.string(),
  }).required().nullable()
    .label('Breed')
    .default(null),
  [FormFields.PetImageUrl]: Yup.string().default('').label('Pet Image'),
});

export type PetInfoPart1FormProps = BoxProps & {
  formik: ReturnType<typeof useFormik>;
};
export const PetInfoPart1Form = ({
  formik,
  ...rest
}: PetInfoPart1FormProps) => {
  const {
    execute: executeGetPetBreedsOption,
    isLoading: isGetPetBreedsQueryLoading,
  } = useGetPetBreedsAutocomplete();
  const [imageUrl, setImageUrl] = useState('');

  const formikFieldHelper = getFormikFieldHelper({
    errors: formik.errors,
    touched: formik.touched,
  });

  return (
    <Box
      as="form"
      display="grid"
      gridGap="8px"
      onSubmit={(event) => {
        formik.handleSubmit(event as FormEvent<HTMLFormElement>);
      }}
      {...rest}
    >
      <TextField
        label="Pet name"
        name={FormFields.PetName}
        value={formik.values[FormFields.PetName]}
        onChange={formik.handleChange}
        data-testid={DataTestIds.Signup.PetInfoForm.PetName}
        onBlur={formik.handleBlur}
        error={formikFieldHelper(FormFields.PetName).hasError}
        helperText={formikFieldHelper(FormFields.PetName).errorMessage}
        fullWidth
      />
      <Datepicker
        label="Pet's est. birthday (mm/dd/yyyy)"
        maxLength={10}
        minLength={10}
        name={FormFields.PetBirthdate}
        value={formik.values[FormFields.PetBirthdate]}
        error={formikFieldHelper(FormFields.PetBirthdate).hasError}
        helperText={formikFieldHelper(FormFields.PetBirthdate).errorMessage}
        onChange={(date) => {
          // date is a `moment` object here
          formik.setFieldValue(FormFields.PetBirthdate, date);
        }}
        onBlur={formik.handleBlur}
        data-testid={DataTestIds.Signup.PetInfoForm.PetBirthdate}
        fullWidth
      />

      <Autocomplete
        defaultOptions
        id={DataTestIds.Signup.PetInfoForm.PetBreed}
        name={FormFields.PetBreed}
        label={isGetPetBreedsQueryLoading ? 'Getting breeds...' : 'Type to search breeds'}
        isLoading={isGetPetBreedsQueryLoading}
        isDisabled={isGetPetBreedsQueryLoading}
        loadOptions={executeGetPetBreedsOption}
        error={formikFieldHelper(FormFields.PetBreed).hasError}
        helperText={formikFieldHelper(FormFields.PetBreed).errorMessage}
        value={formik.values[FormFields.PetBreed]}
        onChange={(value) => {
          // value = null if cleared
          formik.setFieldValue(FormFields.PetBreed, value);
        }}
        onBlur={() => {
          // https://github.com/JedWatson/react-select/issues/3737
          // Issue on mobile devices
          setTimeout(() => {
            // custom on-blur
            formik.handleBlur(FormFields.PetBreed)({
              target: {
                name: FormFields.PetBreed,
              },
            });
          }, 0);
        }}
      />

      {/* <PetImagePickerField
        name={FormFields.PetImageUrl}
        value={imageUrl}
        onChange={(tempImageUrl) => {
          setImageUrl(tempImageUrl);
          formik.setFieldValue(FormFields.PetImageUrl, tempImageUrl);
        }}
        mt="6px"
        data-testid={DataTestIds.Signup.PetInfoForm.PetImageUrl}
      /> */}

      <Divider
        width={1}
      />
      <StepFooter
        primaryCtaText="Next"
        primaryCtaProps={{
          disabled: !formik.isValid || formik.isSubmitting,
          type: 'submit',
          ...{
            'data-testid': DataTestIds.Signup.PetInfoForm.SubmitBtn,
          },
        }}
      />
    </Box>
  );
};
